import { UserRole } from '@/types/types';
import { UsersRoleRedirectMap } from '../consts/consts';
import { WorkspacesService } from '@/services/workspacesService';
import { OrganizationsService } from '@/services/organizationsService';
import { chain, filter, find } from 'lodash';
import { OrganizationFromStore, OrganizationWithWorkspaces } from '@/types/Organization';
import { WorkspaceFromStore } from '@/types/workspaces';
import { isObjectKey } from '@/utils/helpers';

export type GroupedWorkspacesResult = {
  organizations: Record<string, OrganizationWithWorkspaces>;
  workspaces: WorkspaceFromStore[];
};

export function getHomeRedirectUrlPerRole(role?: UserRole): string {
  if (isObjectKey(role, UsersRoleRedirectMap)) {
    return UsersRoleRedirectMap[role];
  }
  return '/connectors';
}

export async function getGroupedWorkspaces(
  userId: string,
): Promise<GroupedWorkspacesResult> {
  const [allWorkspaces, rawOrganizations] = await Promise.all([
    WorkspacesService.getAllWorkspaces(userId),
    OrganizationsService.getOrganizations(userId),
  ]);
  const workspaces = filter(allWorkspaces, 'organizationId');
  if (workspaces.length === 0 && rawOrganizations.length === 0) {
    return { organizations: {}, workspaces: allWorkspaces };
  }
  const organizationsIds = chain(workspaces)
    .map('organizationId')
    .filter()
    .uniq()
    .value();
  const organizations = await OrganizationsService.getOrganizationsByIds(
    organizationsIds,
  );
  const parsedRawOrganizations = chain(rawOrganizations)
    .keyBy('id')
    .mapValues((organization) => ({
      workspaces: [],
      ...organization,
      isOrganizationMember: true,
    }))
    .value();
  const parsedData = {
    ...parsedRawOrganizations,
    ...chain(workspaces)
      .groupBy('organizationId')
      .mapValues((workspaces, organizationId) => {
        const organization = find(organizations, {
          id: organizationId,
        }) as OrganizationFromStore;
        const isOrganizationMember = !!parsedRawOrganizations[organizationId];
        return { workspaces, ...organization, isOrganizationMember };
      })
      .value(),
  };
  return {
    organizations: parsedData as Record<string, OrganizationWithWorkspaces>,
    workspaces: allWorkspaces,
  };
}
